import { ApplicationRef, Inject, Injectable, Injector, Optional, PLATFORM_ID } from "@angular/core";
import { doc, docSnapshots, Firestore } from "@angular/fire/firestore";
import { getFunctions, httpsCallable } from "@angular/fire/functions";
import { Data } from "@angular/router";
import * as CryptoJS from "crypto-js";
import { catchError, firstValueFrom, from, map, of, shareReplay, switchMap, take } from "rxjs";
import { Md5 } from "ts-md5/dist/md5";
import { BaseService } from "../app.base.service";
import { CommonService } from "../services/common/common.service";
import { FirestoreDataService } from "../services/firestore-data.service";
import { HomeDataService } from "../services/home-data.service";
import { CatchErrorService } from "./catch-error.service";
import { isPlatformBrowser } from "@angular/common";
@Injectable({
  providedIn: "root",
})
export class UtilityService extends BaseService {
  static autoShowMessage$: any;
  autoShowMessage$ = this.homeDataService.userInbox$;
  secretKey: string;

  constructor(
    private injector: Injector,

    private firestore: Firestore,
    private homeDataService: HomeDataService,
    private firestoreDataService: FirestoreDataService,
    private catchService: CatchErrorService,
    private commonService: CommonService,
    @Optional() private appRef?: ApplicationRef,
    @Inject(PLATFORM_ID) private platformId?: Object
  ) {
    super(injector);
    this.functions = getFunctions();

  }

  public static autoShowMessage() {
    this.autoShowMessage$?.subscribe(
      (data) => {
        return data?.some((x) => x?.auto_show);
      },
      (err) => {
        return err;
      }
    );
  }
  public loadScript(scriptUrlToLoad, preauth?) {
    for (let i = 0; i < scriptUrlToLoad?.length; i++) {
      if (!scriptUrlToLoad[i]) {
        this.errorHandlerLogs(`loadScript called with missing URL at index ${i} — skipping script injection`, {});
        continue;
      }
      setTimeout(() => {
        let scriptTag = document.createElement("script");
        scriptTag.src = scriptUrlToLoad[i];
        if (preauth) {
          scriptTag.id = "pwaPayment"
        }
        
        // Add error handler to catch script loading errors and errors from third-party scripts
        scriptTag.onerror = (error) => {
          this.errorHandlerLogs(`Failed to load script: ${scriptUrlToLoad[i]}`, error);
          // Prevent the error from propagating and crashing the app
          return true;
        };
        
        // Add load handler to catch runtime errors from the loaded script
        scriptTag.onload = () => {
          // Add global error handler for errors from this script
          const originalOnError = window.onerror;
          window.onerror = (message, source, lineno, colno, error) => {
            // Check if error is from the loaded script
            if (source && source.includes(scriptUrlToLoad[i])) {
              this.errorHandlerLogs(`Runtime error in loaded script: ${scriptUrlToLoad[i]}`, {
                message,
                source,
                lineno,
                colno,
                error
              });
              // Prevent error from crashing the app
              return true;
            }
            // Call original error handler if it exists
            if (originalOnError) {
              return originalOnError(message, source, lineno, colno, error);
            }
            return false;
          };
        };
        
        let scripts = document.getElementsByTagName("script");
        if (scripts?.length > 0) {
          scripts[0].parentNode.insertBefore(scriptTag, scripts[0]);
        } else {
          if (scripts) document.body?.appendChild(scriptTag);
        }
      }, 3000);
    }
  }

  /**
   * Safely check if an iframe is same-origin before accessing its contentWindow
   * Prevents SecurityError on cross-origin iframes, especially on iOS Safari
   * @param iframe HTMLIFrameElement to check
   * @returns true if iframe is same-origin and safe to access, false otherwise
   */
  public static isSameOriginIframe(iframe: HTMLIFrameElement): boolean {
    try {
      // Accessing contentWindow on cross-origin iframe throws SecurityError
      // This try-catch pattern safely detects cross-origin iframes
      if (!iframe) return false;
      
      // Attempt to access a simple property - if it throws SecurityError, it's cross-origin
      const _ = iframe.contentWindow;
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'SecurityError') {
        // Cross-origin iframe - safe to catch
        return false;
      }
      // Other errors should still be handled
      return false;
    }
  }

  /**
   * Safely focus an iframe using fallback methods for cross-origin iframes
   * On cross-origin iframes, contentWindow.focus() throws SecurityError,
   * so we fall back to iframe.focus() which works across origins
   * @param iframe HTMLIFrameElement to focus
   */
  public static safeFocusIframe(iframe: HTMLIFrameElement): void {
    if (!iframe) return;
    
    try {
      // Try the primary method first (only works for same-origin)
      if (this.isSameOriginIframe(iframe)) {
        iframe.contentWindow?.focus();
      } else {
        // For cross-origin iframes, use the element focus method
        iframe.focus();
      }
    } catch (error) {
      // Silently handle any focus errors
      if (error instanceof Error) {
        console.debug('[SafeFocusIframe] Unable to focus iframe:', error.name, error.message);
      }
    }
  }

  public checkIframeInputFocus() {
    try {
      const cvvInterval = setInterval(() => {

        const cvvIframe = document.querySelector(".wpwl-control-cvv") as HTMLIFrameElement;
        const cvvParentElement = document.querySelector(".wpwl-group-cvv");

        if (cvvParentElement && cvvIframe) {
          clearInterval(cvvInterval);
          // Attach click event listener to the parent element
          cvvParentElement.addEventListener("click", () => {
            try {
              // Use safe focus method that handles cross-origin iframes
              UtilityService.safeFocusIframe(cvvIframe);
            } catch (error) {
              this.errorHandlerLogs('Error focusing CVV iframe:', error);
            }
          });
        }
      }, 1000)

      const cardNumberInterval = setInterval(() => {

        const cardNumberIframe = document.querySelector(".wpwl-control-cardNumber") as HTMLIFrameElement;
        const cardNumberParentElement = document.querySelector(".wpwl-group-cardNumber");

        if (cardNumberParentElement && cardNumberIframe) {
          clearInterval(cardNumberInterval);

          // Attach click event listener to the parent element
          cardNumberParentElement.addEventListener("click", () => {
            try {
              // Use safe focus method that handles cross-origin iframes
              UtilityService.safeFocusIframe(cardNumberIframe);
            } catch (error) {
              this.errorHandlerLogs('Error focusing card number iframe:', error);
            }
          });
        }
      }, 1000);
    } catch (error) {
      return error
    }
  }

  public loadScriptForPayment(scriptUrlToLoad, preauth?) {
    for (let i = 0; i < scriptUrlToLoad?.length; i++) {
      if (!scriptUrlToLoad[i]) {
        this.errorHandlerLogs(`loadScriptForPayment called with missing URL at index ${i} — skipping script injection`, {});
        continue;
      }
      let scriptTag = document.createElement("script");
      scriptTag.src = scriptUrlToLoad[i];
      if (preauth) {
        scriptTag.id = "pwaPayment";
      }
      
      // Add error handler to catch script loading errors and errors from third-party scripts
      scriptTag.onerror = (error) => {
        this.errorHandlerLogs(`Failed to load payment script: ${scriptUrlToLoad[i]}`, error);
        // Prevent the error from propagating and crashing the app
        return true;
      };
      
      // Add load handler to catch runtime errors from the loaded script
      scriptTag.onload = () => {
        // Add global error handler for errors from this script
        const originalOnError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
          // Check if error is from the loaded script
          if (source && source.includes(scriptUrlToLoad[i])) {
            this.errorHandlerLogs(`Runtime error in loaded payment script: ${scriptUrlToLoad[i]}`, {
              message,
              source,
              lineno,
              colno,
              error
            });
            // Prevent error from crashing the app
            return true;
          }
          // Call original error handler if it exists
          if (originalOnError) {
            return originalOnError(message, source, lineno, colno, error);
          }
          return false;
        };
      };
      
      let scripts = document.getElementsByTagName("script");
      if (scripts?.length > 0) {
        scripts[0].parentNode.insertBefore(scriptTag, scripts[0]);
      } else {
        if (scripts) document.body.appendChild(scriptTag);
      }
    }
  }

  public static createHash(phoneNumber: string, type, env) {
    let md5 = new Md5();

    md5.appendStr(phoneNumber + type + env + "a23DFFdf51f2opeHFKsMCi3Sfkj4fsldksosdfmK");
    let hash = md5.end();
    return hash;
  }

  public static createHashForVerifyOtp(phoneNumber: string, otp, type, env) {
    let md5 = new Md5();
    md5.appendStr(phoneNumber + String(otp) + type + env + "a23DFFdf51f2opeHFKsMCi3Sfkj4fsldksosdfmK");
    let hash = md5.end();
    return hash;
  }
  public static rapidFireSortingGamesPage(rapidFireGames: Data, gamesOrder: Data) {
    try {
      if (!rapidFireGames || (!Array.isArray(gamesOrder) && !gamesOrder?.length)) return;
      const sortedRapidFireGames = rapidFireGames?.sort((first, second) => gamesOrder?.indexOf(first?.id) - gamesOrder?.indexOf(second?.id));
      const filteredGamesOrder = rapidFireGames?.filter((element) => gamesOrder?.includes(element?.id));
      const filteredRapidFireGame: number[] = sortedRapidFireGames?.filter((element) => !gamesOrder?.includes(element?.id));
      const mergedGames: number[] = filteredGamesOrder?.concat(filteredRapidFireGame);
      return mergedGames;
    } catch (error) {
      throw new Error("Error in sorting Rapid Fire Games " + error);
    }
  }

  public static jackpotSortingStorePage(lotteryProducts: any, sortedArrayField: any) {
    if (!lotteryProducts || (!Array.isArray(sortedArrayField) && !sortedArrayField?.length)) return;
    lotteryProducts.sort(
      (x, y) =>
        sortedArrayField?.findIndex((item1) => x?.real_name?.toLowerCase() === item1?.toLowerCase()) -
        sortedArrayField?.findIndex((item2) => y?.real_name?.toLowerCase() === item2?.toLowerCase())
    );
    return lotteryProducts;
  }

  public static jackpotSortingStorePageOld(lotteryProducts: any) {
    if (!lotteryProducts) return;
    let preEuroIndex = lotteryProducts?.findIndex((x) => x?.real_name?.toLowerCase() === "euromillions");
    lotteryProducts.splice(
      0,
      0,
      lotteryProducts?.find((x) => x?.real_name?.toLowerCase() === "euromillions")
    );
    lotteryProducts.splice(preEuroIndex + 1, 1);
    let preLottoIndex = lotteryProducts?.findIndex((x) => x?.real_name?.toLowerCase() === "lotto");
    lotteryProducts.splice(
      1,
      0,
      lotteryProducts?.find((x) => x?.real_name?.toLowerCase() === "lotto")
    );
    lotteryProducts.splice(preLottoIndex + 1, 1);

    lotteryProducts.sort(function (a, b) {
      if (
        !a.jackpot ||
        !b.jackpot ||
        a?.real_name?.toLowerCase() === "" ||
        a?.real_name?.toLowerCase() === "lotto" ||
        b?.real_name?.toLowerCase() === "euromillions" ||
        b?.real_name?.toLowerCase() === "lotto"
      ) {
        return;
      }
      return (
        b?.jackpot.jackpot_value.slice(0, b.jackpot.jackpot_value?.length - 1) -
        a?.jackpot.jackpot_value.slice(0, a.jackpot.jackpot_value?.length - 1)
      );
    });
    return lotteryProducts;
  }

  public static jackpotSortingHomePage(allJackpots: any) {
    if (!allJackpots) return;
    let sortedjackpot = [];
    if (!allJackpots) return;
    sortedjackpot[0] = allJackpots?.find((x, i) => {
      if (x?.real_name?.toLowerCase() === "euromillions") {
        allJackpots.splice(i, 1);
      }
      return x?.real_name?.toLowerCase() === "euromillions";
    });
    sortedjackpot[1] = allJackpots?.find((x, i) => {
      if (x?.real_name?.toLowerCase() === "lotto") {
        allJackpots.splice(i, 1);
      }
      return x?.real_name?.toLowerCase() === "lotto";
    });
    allJackpots.sort(function (a, b) {
      return (
        b.jackpot_value?.slice(0, b.jackpot_value?.length - 1) - a.jackpot_value?.slice(0, a.jackpot_value?.length - 1)
      );
    });
    sortedjackpot = sortedjackpot.concat(allJackpots);
    return sortedjackpot;
  }

  public static shortDrawDay(day) {
    if (day === "Monday") {
      return "Mon";
    } else if (day === "Tuesday") {
      return "Tue";
    } else if (day === "Wednesday") {
      return "Wed";
    } else if (day === "Thursday") {
      return "Thu";
    } else if (day === "Friday") {
      return "Fri";
    } else if (day === "Saturday") {
      return "Sat";
    } else if (day === "Sunday") {
      return "Sun";
    }
  }

  public static msnValidation(msn: any) {
    if (msn === "") {
      return "Please enter your mobile number.";
    } else if (msn !== "" && !this.validateNumeric(msn)) {
      return "Please enter a valid Mobile Number.";
    } else if (msn?.length < 5) {
      return "Mobile number too short, please enter a valid number.";
    } else if (msn?.length > 12) {
      return "Mobile number too long, please enter a valid number.";
    }
    return "";
  }

  public static passwordValidation(password: any) {
    if (password === "") {
      return "Password enter your password..";
    } else if (password?.length < 6) {
      return "Password must contain at least 6 characters..";
    } else if (this.validate_pwd(password) === true) {
      return "Password contains invalid characters.";
    }
  }

  private static validateNumeric(strValue) {
    const objRegExp = /(^-?\d\d*\.\d*$)|(^-?\d\d*$)|(^-?\.\d\d*$)/;
    return objRegExp.test(strValue);
  }

  private static validate_pwd(name: string) {
    const nameRegex = /[^a-zA-Z0-9]/;
    return nameRegex.test(name?.toLowerCase());
  }

  public static firstNameValidation(Name: any, type: string) {
    if (Name === "" || Name?.toLowerCase() === "null") {
      return "Please enter your " + type + " name.";
    } else if (!this.validate_name(Name)) {
      return type + "name should be alphabets.";
    } else if (Name?.length > 20) {
      return type + "name should be maximum of 20 characters.";
    }
  }

  public static emailValidation(email) {
    if (email === "") {
      return "Please enter a valid email address.";
    } else if (!this.validateEmail(email)) {
      return "Please enter a valid email address.";
    }
  }

  private static validate_name(name_value) {
    const nameRegex = /^[a-zA-Z]+(([\_\- ][a-zA-Z ])?[a-zA-Z]*)*$/;
    return name_value.match(nameRegex);
  }

  private static validateEmail(strValue) {
    const objRegExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return objRegExp.test(strValue);
  }

  public static nickNameValidation(nickName) {
    const alphaRegex = /^[a-zA-Z\s]*$/;
    const spaceRegex = /\s+/g;
    if (nickName === "" || nickName === undefined || nickName == null) {
      return "Nickname can not be null";
    } else if (!alphaRegex.test(nickName)) {
      return "Nickname accepts only words";
    } else if (spaceRegex.test(nickName)) {
      return "Nickname shouldn't have space";
    }
  }

  public static isNull(inputValue: any, inputName: string) {
    if (inputValue === "" || inputValue === null || inputValue === undefined) {
      return inputName + " can't be null.";
    }
  }

  public static insertString(index: number, textToAppend: string, originalText): string {
    if (index > 0) {
      try {
        return originalText?.substring(0, index) + textToAppend + originalText?.substring(index, originalText?.length);
      } catch (e) { }
    }

    return textToAppend;
  }

  // if are standalone android OR safari
  public static isAppInstalledOnHomePage() {
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator["standalone"] && window.navigator["standalone"] === true)
    ) {
      return true;
    }
    return false;
  }

  public static getLeftDays(dateExpiry: Date) {
    // The number of milliseconds in one day
    const ONE_DAY = 1000 * 60 * 60 * 24;

    // Calculate the difference in milliseconds
    const differenceMs = Math.abs(dateExpiry.getTime() - new Date().getTime());
    // Convert back to days and return
    return Math.round(differenceMs / ONE_DAY);
  }

  public static chatUrlFormation(syndicate: any) {
    let url = "/&peepso_api=1&owner_id=";
    url += localStorage.getItem("wp_user_id") ? localStorage.getItem("wp_user_id") : 22;
    url = "&token=" + syndicate?.customToken;
    return syndicate?.peepso_group_url + url;
  }

  public static helpUrlFormation() {
    return (
      "https://lottosocial.com/webview-auth/?redirect_to=help-faq" +
      "&auto_login_token_validate=" +
      localStorage.getItem("auto_login_token_validate") +
      "&time=" +
      new Date().getTime()
    );
  }

  public static processErrorMessage(errorCode: string) {
    if (errorCode == "001") {
      return "We’ve found an error, It looks like this game play has an issue. If you have other plays, please try again. If you think there is a mistake, speak to support.";
    } else if (errorCode == "002") {
      return "We’ve found an error, You are already playing or have no plays left of this game.If you think this is incorrect , contact support via the help center.";
    } else if (errorCode == "003") {
      return "We’ve found an error, You have reached the maximum limit of play per day. If you do not think this is correct please contact support via the Help Center.";
    } else if (errorCode == "004") {
      return "We’ve found an error, Oops there was an issue with your play. You can play your unlock game again by going to game Tab";
    } else if (errorCode == "005") {
      return "We’ve found an error, You have reached the maximum limit of Cash Win per day. If you do not think this is correct please contact support via the Help Center.";
    } else if (errorCode == "006") {
      return "We’ve found an error, You are already playing or have no plays left of this game. If you think this is incorrect , contact support via the help center.";
    } else if (errorCode == "007") {
      return "We’ve found an error, You can not play this Adventure at this time. Please try again/contact support via help center";
    } else if (errorCode == "008") {
      return "We’ve found an error, You dont have any active lottery products. Please try to purchase and play another time.";
    } else if (errorCode == "009") {
      ("We’ve found an error, There is an issue with your account. Please contact support.");
    } else if (errorCode == "010") {
      return "We’ve found an error, There is an issue with your account. Please contact support.";
    } else if (errorCode == "011") {
      return "We’ve found an error, There is an issue with your account. Please contact support.";
    } else if (errorCode == "012") {
      return "We’ve found an error, Your adventure tournament already expired/over";
    } else if (errorCode == "90008") {
      return "We’ve found an error, This game is currently under review and can not be played. Please try and play another time.";
    } else {
      return "We’ve found an error, It looks like this game play has an issue. If you have other plays, please try again. If you think there is a mistake, speak to support";
    }
  }

  // Check minutes difference between 2 dates
  public diffMinutes(dt1: Date, dt2: Date) {
    let diff = (dt2.getTime() - dt1.getTime()) / 1000;
    diff /= 60;
    return Math.abs(Math.round(diff));
  }

  // This function is used to seed the cloud function with user id.
  seedUserData(userId) {
    return this.firestoreDataService.getV2CloudFunction("seedv2", userId, { timeout: 180000 });
  }


  //get dynamic data from cloud function
  processDynamicVariable(requestData: any) {
    return this.firestoreDataService.getV2CloudFunction("processdynamicvariablesv2", requestData);
  }


  // Get cookie value of given name
  getCookie(name) {
    try {
      var nameEQ = name + "=";
      var cookie = document.cookie?.split(";");
      for (var i = 0; i < cookie?.length; i++) {
        var c = cookie[i];
        while (c.charAt(0) == " ") c = c?.substring(1, c?.length);
        if (c?.indexOf(nameEQ) == 0) return c?.substring(nameEQ?.length, c?.length);
      }
      return null;
    } catch (e) {
      return e;
    }
  }

  // This function is triggered when the user clicks the "translate" button in the UI
  // The function takes the user's ID and passes it to the Cloud Function
  translatevoucher() {
    this.commonService.getUid("translatevoucher").pipe(take(1)).subscribe((userId) => {
      if (!userId) return;

      const data = { userId };
      this.firestoreDataService.getV2CloudFunction("uservouchertranslationv2", data)?.subscribe();
    });
  }


  // This function is called when the user clicks the button to translate their psinvitation. It calls the firebase function psinvitationTranslation

  translatePsInvitiation() {
    return this.commonService.getUid("translatePsInvitiation").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null);

        const psinvitationTranslation = httpsCallable(this.functions, "psinvitationTranslation");
        return from(psinvitationTranslation({ userId })).pipe(
          map((result: any) => result.data), // Assuming 'data' contains the translated info
          catchError((error) => {
            console.error("Error during translation:", error);
            return of(null);
          })
        );
      })
    );
  }
  // This method is used to safely trigger global change detection
  private isPending = false;
  runChangeDetection(): boolean {
    if (!isPlatformBrowser(this.platformId) || !this.appRef) return false; // skip on SSR or missing appRef
    if (this.isPending) return false; // prevent flooding
    this.isPending = true;
    // trigger change detection
    queueMicrotask(() => {
      try {
        this.appRef?.tick(); // global CD trigger
      } catch (err) {
        this.errorHandlerLogs('Change detection skipped:', err);
      } finally {
        this.isPending = false;
      }
    });
    return true;
  }


  // This function retrieves the list of languages from the database.
  // The list of languages is used to populate a dropdown list in the settings page.
  getLanguageList() {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return null;

        const docRef = doc(
          this.firestore,
          `/general_modules/language` // Firestore document reference
        );

        return docSnapshots(docRef).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    ).pipe(
      map((actions: any) => actions?.data()), // Updated mapping for document data
      shareReplay(1)
    );
  }
  // This method is used to log error messages
  errorHandlerLogs(message: string, error?: unknown): { message: string; timestamp: string; error: Data } {
    const log = {
      message,
      timestamp: new Date().toISOString(),
      error: error ? this.formatError(error) : null,
    };
    return log;

  }

  // This method is used to format error messages
  private formatError(error: Data): Data {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    return error;
  }
}

export const isMobile = {
  Android: function () {
    return navigator.userAgent.match(/Android/i);
  },
  BlackBerry: function () {
    return navigator.userAgent.match(/BlackBerry/i);
  },
  iOS: function () {
    return navigator.userAgent.match(/iPhone|iPad|iPod/i);
  },
  Opera: function () {
    return navigator.userAgent.match(/Opera Mini/i);
  },
  Windows: function () {
    return navigator.userAgent.match(/IEMobile/i);
  },
  any: function () {
    return isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Opera() || isMobile.Windows();
  },
};

export const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen?.add(value);
    }
    return value;
  };
};

// Add the extractSerializableData utility function
export const extractSerializableData = (obj: Data): Data => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle basic types
  if (typeof obj !== 'object') {
    return obj;
  }

  // Skip functions and Firebase references
  if (
    typeof obj === 'function' ||
    (obj && obj.onSnapshot && typeof obj.onSnapshot === 'function') ||
    (obj && obj.get && typeof obj.get === 'function' && obj.firestore)
  ) {
    return null;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => extractSerializableData(item));
  }

  // Handle objects
  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      // Skip functions and Firebase references
      if (
        typeof value !== 'function' &&
        !(value && value.onSnapshot && typeof value.onSnapshot === 'function') &&
        !(value && value.get && typeof value.get === 'function' && value.firestore)
      ) {
        result[key] = extractSerializableData(value);
      }
    }
  }
  return result;
};

export const removejscssfile = (filename, filetype) => {
  var targetelement = filetype == "js" ? "script" : filetype == "css" ? "link" : "none"; //determine element type to create nodelist from
  var targetattr = filetype == "js" ? "src" : filetype == "css" ? "href" : "none"; //determine corresponding attribute to test for
  var allsuspects = document.getElementsByTagName(targetelement);
  for (var i = allsuspects?.length; i >= 0; i--) {
    //search backwards within nodelist for matching elements to remove
    if (
      allsuspects[i] &&
      allsuspects[i]?.getAttribute(targetattr) != null &&
      allsuspects[i]?.getAttribute(targetattr)?.indexOf(filename) != -1
    )
      if (!allsuspects[i] || !allsuspects[i].parentNode) return;
    allsuspects[i].parentNode.removeChild(allsuspects[i]); //remove element by calling parentNode.removeChild()
  }
};
const salt = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOiI1ZDg5MjMxMjc5OTkxYjJhNGMwMjdjMGIiLCJoc2giOiIkMmEkMTMkWk53Y0cubjdRZFIybDA3S1RHd2RoLlN0QksudW5GSFVGLkZnZ0tQTGlUV2pOVEFqVy9SMm0iLCJncmFudCI6ImFjY2VzcyIsImlhdCI6MTU2OTI2ODUwMiwiZXhwIjoxNjAwODI2MTAyfQ.PQcCoF9d25bBqr1U4IhJbylpnKTYiad3NjCh_LvMfLE~9~null~undefined~434ce0149ce42606d8746bd9`;

export const encryptUsingAES256 = (token: any) => {
  const cryptoInfo = CryptoJS.AES.encrypt(JSON.stringify(token), salt);
  return cryptoInfo.toString();
};
export const decryptUsingAES256 = (encryptedText: any) => {
  const info2 = CryptoJS.AES.decrypt(encryptedText.toString(), salt).toString(CryptoJS?.enc?.Utf8);
  return info2;
};
